import { inject, observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import React from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import LinearProgress from "@material-ui/core/LinearProgress";
import { Typography } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { DEFAULT_UNSET_WATERMARK } from "../httpClients/ClippyApi";
import Video from "./Video";
import VideoPlaceholder from "./VideoPlaceholder";
import ForgeDestinationCheckboxes from "./ForgeDestinationCheckboxes";
import { CMS_DESTINATION_LABEL } from "../utils/constants";

const styles = theme => ({
  flexDialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    alignItems: "start",
    justifyContent: "flex-start",
    minHeight: "400px"
  },
  centeredItem: {
    alignSelf: "center"
  },
  horizontalFlexContent: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
    alignItems: "flex-start",
    gap: theme.spacing(4)
  },
  dialogActions: {
    padding: theme.spacing(2)
  },
  durationBox: {
    position: "absolute",
    right: "24px",
    color: "white",
    background: "rgba(0,0,0,.7)",
    top: "20px",
    padding: "4px 6px",
    borderRadius: "5px"
  },
  videoPlaceholderContainer: {
    height: "200px",
    width: "400px"
  }
});

const ITEM_REMOVED = "remove-option";
const ALL_ITEMS_REMOVED = "clear";
const ITEM_SELECTED = "select-option";

class QuickerClipDialog extends React.Component {
  render() {
    const {
      quickerClipStore,
      typeaheadDropDownStore,
      quickerClipForgeStore,
      watermarkStore,
      gameMetadataStore,
      sportsReferenceStore,
      classes
    } = this.props;

    const forgeVideoPost = quickerClipForgeStore.forgeVideoPost;

    const clearAutoCompleteSearchData = () => {
      typeaheadDropDownStore.clearSuggestions();
      typeaheadDropDownStore.clearPreviousSelections();
    };

    // Watermarks returned as filenames  or "None" (default)
    // Helper method to format for readability (e.g. "xyz.png" -> "XYZ")
    const formatWatermarkName = name =>
      name !== DEFAULT_UNSET_WATERMARK
        ? name.split(".")[0].toUpperCase()
        : name;

    const handleOnInputChange = (value, reason) => {
      if (reason === "input") {
        if (value) {
          typeaheadDropDownStore.fetchTags(value);
        }
      } else if (reason === ALL_ITEMS_REMOVED) {
        quickerClipForgeStore.selectedTags = [];
        clearAutoCompleteSearchData();
      }
    };

    // the user selected or removed something.
    const handleTypeAheadOnChange = (event, values, detail) => {
      if (detail === ITEM_REMOVED) {
        quickerClipForgeStore.selectedTags = values; // These are the remaining chips
        typeaheadDropDownStore.resetPreviousSelections(values);
      } else if (detail === ITEM_SELECTED) {
        // add another selection, if we haven't already added it.
        values.forEach(v => {
          quickerClipForgeStore.addTag(v);
        });
      }
      typeaheadDropDownStore.clearSuggestions();
    };

    return (
      <Dialog
        open={quickerClipStore.isDialogOpen}
        onClose={() => quickerClipStore.closeDialog()}
        scroll="paper"
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <div>
            <Typography variant="h5" component="span">
              Send To Forge
            </Typography>
            {!quickerClipStore.isCreatingForgeClip && (
              <Typography className={classes.durationBox}>
                {forgeVideoPost.formattedDuration}
              </Typography>
            )}
          </div>
        </DialogTitle>
        <DialogContent className={classes.flexDialogContent}>
          {quickerClipStore.isCreatingForgeClip ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                width: "100%"
              }}
            >
              {quickerClipStore.isCreatingClip && (
                <Typography variant="h6">Creating Clip...</Typography>
              )}
              {quickerClipForgeStore.isSendingClipToForge &&
                quickerClipForgeStore.sendingDestination && (
                  <Typography variant="h6">
                    {`Sending Clip to ${
                      CMS_DESTINATION_LABEL[
                        quickerClipForgeStore.sendingDestination
                      ]
                    }...`}
                  </Typography>
                )}
              <CircularProgress style={{ marginTop: "1rem" }} />
              <LinearProgress
                variant="determinate"
                value={quickerClipStore.forgeProgressPercent}
                style={{ width: "60%", marginTop: "1rem" }}
              />
            </div>
          ) : (
            <React.Fragment>
              <div className={classes.centeredItem}>
                {forgeVideoPost.thumbnailUrl &&
                forgeVideoPost.thumbnailUrl !== "" ? (
                  <Video
                    thumbnailUri={forgeVideoPost.thumbnailUrl}
                    src={forgeVideoPost.videoUrl}
                    handleVideoMetadata={video =>
                      quickerClipStore.addClipMetadata(video)
                    }
                  />
                ) : (
                  <div className={classes.videoPlaceholderContainer}>
                    <VideoPlaceholder />
                  </div>
                )}
              </div>

              <TextField
                required
                autoFocus
                fullWidth
                className={classes.videoTitle}
                margin="dense"
                label="Title"
                value={quickerClipStore.clipTitle}
                helperText={
                  quickerClipStore.titleError ? "Title is required" : ""
                }
                onChange={e => quickerClipStore.updateTitle(e.target.value)}
              />

              <TextField
                margin="dense"
                label="Description"
                fullWidth
                className={classes.videoDescription}
                value={forgeVideoPost.videoDescription}
                onChange={e => {
                  forgeVideoPost.videoDescription = e.target.value;
                }}
              />

              <Autocomplete
                multiple
                fullWidth
                style={{ marginTop: "1rem" }}
                value={quickerClipForgeStore.selectedTags}
                options={typeaheadDropDownStore.suggestions.slice()} // convert to JS array instead of MobX
                getOptionSelected={(option, value) => {
                  return option.value === value.value;
                }}
                getOptionLabel={option => {
                  return option && option.label ? option.label : "";
                }}
                id={`auto-complete-forge-tags`}
                onInputChange={(event, value, reason) =>
                  handleOnInputChange(value, reason)
                }
                onChange={(event, value, detail) =>
                  handleTypeAheadOnChange(event, value, detail)
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    onKeyDown={e => e.stopPropagation()}
                    label={"Tags"}
                    helperText={"Begin typing to see suggestions"}
                    onBlur={() => {
                      typeaheadDropDownStore.clearSuggestions();
                    }}
                    InputProps={{
                      ...params.InputProps,
                      type: "search"
                    }}
                  />
                )}
              />

              <Grid
                container
                style={{ marginTop: "1rem", paddingBottom: "1.5rem" }}
              >
                <Grid item xs={3} style={{ paddingRight: "8px" }}>
                  <InputLabel id="sport-id-label" shrink={true}>
                    Sport
                  </InputLabel>
                  <Select
                    id="sport-selector-select"
                    label="Sport"
                    labelId={"sport-id-label"}
                    value={gameMetadataStore.sportId}
                    onChange={e => gameMetadataStore.setSportId(e.target.value)}
                    fullWidth
                  >
                    {sportsReferenceStore.sports.map(s => (
                      <MenuItem key={s.code} value={s.id}>
                        {s.abbreviation}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>

                <Grid item xs={3} style={{ paddingRight: "8px" }}>
                  <TextField
                    id="date"
                    label="Date"
                    type="date"
                    value={gameMetadataStore.date.toISOString().slice(0, 10)}
                    onChange={e => {
                      gameMetadataStore.setDate(new Date(e.target.value));
                    }}
                    fullWidth
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>

                <Grid item xs={6}>
                  <InputLabel id="game-label" shrink={true}>
                    Game
                  </InputLabel>
                  <Select
                    id="game-selector-select"
                    label="Game"
                    labelId={"game-label"}
                    defaultValue={""}
                    disabled={gameMetadataStore.games.length === 0}
                    onChange={e => {
                      const tag = `gamepk-${e.target.value}`;
                      const tagOption = { label: tag, value: tag };
                      quickerClipForgeStore.addTag(tagOption);
                    }}
                    fullWidth
                  >
                    {gameMetadataStore.games.map(g => (
                      <MenuItem key={g.gamePk} value={g.gamePk}>
                        {g.description}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </Grid>
              <div className={classes.horizontalFlexContent}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={quickerClipStore.applyCrossfade}
                      onChange={e =>
                        (quickerClipStore.applyCrossfade = e.target.checked)
                      }
                      value="crossfade"
                    />
                  }
                  label="Apply Crossfade"
                />
                <FormControl style={{ minWidth: "7rem", marginLeft: "1rem" }}>
                  <InputLabel id="crop-label">Crop Ratio</InputLabel>
                  <Select
                    variant="standard"
                    labelId="crop-label"
                    label="Crop Ratio"
                    value={quickerClipStore.cropRatio}
                    onChange={e =>
                      quickerClipStore.setCropRatio(e.target.value)
                    }
                  >
                    {quickerClipStore.cropRatios.map((c, i) => (
                      <MenuItem key={i} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl style={{ minWidth: "7rem", marginLeft: "1rem" }}>
                  <InputLabel id="watermark">Watermark</InputLabel>
                  <Select
                    variant="standard"
                    labelId="watermark"
                    label="Watermark"
                    value={quickerClipStore.watermark}
                    onChange={e =>
                      quickerClipStore.setWatermark(e.target.value)
                    }
                  >
                    {watermarkStore.watermarks.map((c, i) => (
                      <MenuItem key={i} value={c}>
                        {formatWatermarkName(c)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div style={{ marginLeft: "auto" }}>
                  <ForgeDestinationCheckboxes store={quickerClipForgeStore} />
                </div>
              </div>
            </React.Fragment>
          )}
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            variant="contained"
            color="primary"
            style={{ marginRight: "20px" }}
            onClick={async () => await quickerClipStore.createAndSendClip()}
            disabled={
              quickerClipStore.reelClips.length === 0 ||
              quickerClipStore.isCreatingForgeClip ||
              !quickerClipForgeStore.hasValidDestinationSelection
            }
          >
            Send To Forge
          </Button>
          <Button onClick={() => quickerClipStore.closeDialog()}>Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default inject(
  "quickerClipStore",
  "quickerClipForgeStore",
  "watermarkStore",
  "typeaheadDropDownStore",
  "gameMetadataStore",
  "sportsReferenceStore"
)(withStyles(styles)(observer(QuickerClipDialog)));



// WEBPACK FOOTER //
// ./src/components/QuickerClipDialog.js